/* eslint-disable react/no-unstable-nested-components */
import { ColumnDef, SortingState } from '@tanstack/react-table';
import IconList, { IconListModifier } from 'components/iconList';
import { useTranslation } from 'react-i18next';
import BalanceStatusTooltip, { BalanceStatusEnum } from 'pages/overview/balanceStatusTooltip';
import {
  useContext, useMemo, useState,
} from 'react';
import Tooltip, { TooltipAlignEnum } from 'components/tooltip';
import { useLocation } from 'react-router-dom';
import { NumericFormat } from 'react-number-format';
import BalanceTableFilters from './filters/balanceTableFilters';
import { StoreContext } from '../../models/import';
import { TableBaseApiKind, TableDataRes } from './base/tableBaseApi';
import LegacyTableBase, { getDefaultEmptyValue } from './base/legacyTableBase';
import TableBase, { TableModifier } from './base/tableBase';
import { OverviewLeftTableDef } from './overviewLeftTable';
import PlatformCard, { PlatformCardModifier } from 'components/platformCard';
import { getImportTableBalanceStatus } from './importTable';

export interface LocationHistoryMismatch {
  data: boolean | undefined;
  dataCurrency: boolean | undefined;
  tableChanged: boolean | undefined;
}

export interface BalanceTableProps { // implements TableWrapperProps
  filters: string;
  pageSize?: number;
  requestUri?: string;
  onErrorCB?: (e: unknown) => void;
  setCurrentMismatch?: (value: boolean) => void;
  setUserCurrencyMismatch?: (value: boolean) => void;
}

export interface BalanceTableDef {
  coin_name: string | null;
  coin_logo_uri: string | null;
  coin_ticker: string;
  summarized_custom_names: string[];
  live_balance_spot: number | null;
  live_balance_fiat: number | null;
  imported_balance_spot: number;
  imported_balance_fiat: number;
  difference_spot: number | null;
  difference_fiat: number | null;
  status: number;
}

// TODO !!
const getTrailingZerosFormattedNumber = (
  value: string,
  decimalSeparator: string,
) : string | (string | JSX.Element)[] => {
  const valueParts: string[] = value.split(decimalSeparator, 2);
  const integers: string = valueParts[0];
  const decimals: string = valueParts[1];
  if (/^0+$/.test(decimals)) {
    return [integers,
      <span key={integers} className="ct_tableBase-value_isTrailingZero">
        {decimalSeparator}
        {decimals}
      </span>];
  }
  const decimalParts: string[] | null = decimals.match(/^(\d*[1-9]+)(0+)$/);
  if (decimalParts) {
    const commonDecimals: string = decimalParts[1];
    const trailingZeros: string = decimalParts[2];
    return [integers, decimalSeparator, commonDecimals,
      <span key={integers} className="ct_tableBase-value_isTrailingZero">
        {trailingZeros}
      </span>,
    ];
  }
  return value;
};

export const getFormattedNumber = (
  value: number | null,
  decimalSeparator: string,
  thousandSeparator: string,
  currency: string | null = null,
) : null | JSX.Element => {
  if (value === null) {
    return getDefaultEmptyValue();
  }

  const negativeValueClass: string = (value < 0) ? 'ct_tableBase-value_isNegative' : '';
  const onlyTwoDecimalsCurrency: boolean = ['EUR', 'USD'].includes(currency || '');

  return (
    <NumericFormat
      value={value}
      decimalScale={onlyTwoDecimalsCurrency ? 2 : 8}
      fixedDecimalScale={onlyTwoDecimalsCurrency}//if fiat always show zeros
      decimalSeparator={decimalSeparator}
      thousandSeparator={thousandSeparator}
      displayType="text"
      renderText={(renderedValue: string) => (
        <span className={(negativeValueClass)}>
          {
            renderedValue
          }
        </span>
      )}
    />
  );
};

function BlanaceTable(props: BalanceTableProps) {
  const location = useLocation();
  const { pageSize = 100, requestUri = 'balanceTableData', onErrorCB } = props;
  const { t } = useTranslation();
  const { userSettings } = useContext(StoreContext);
  const [filters, setFilters] = useState('');

  const columnVisibility = {
    coin_name: false,
    coin_logo_uri: false,
    live_balance_fiat: false,
    imported_live_balance_fiat: false,
    imported_balance_fiat: false,
  };

  const initialSorting: SortingState = [{
    id: 'coin_ticker',
    desc: false,
  }];

  const handleError = (e: unknown) => {
    if (onErrorCB) onErrorCB(e);
  };

  const balanceTableColumns: ColumnDef<BalanceTableDef>[] = useMemo(() => [
    {
      accessorKey: 'coin_logo_uri',
    },
    {
      accessorKey: 'coin_name',
    },
    {
      accessorKey: 'coin_ticker',
      cell: (info) => {
        const coinTicker: string = info.getValue() as string | '';
        const coinName: string | null = info.row.original.coin_name;
        const coinLogoUri: string | null = info.row.original.coin_logo_uri;

        return (
          <Tooltip align={TooltipAlignEnum.LEFT} message={coinName ? coinName : undefined}>
          <PlatformCard
            logo_uri={coinLogoUri ? coinLogoUri : undefined}
            name={coinTicker}
            display_name={coinTicker}
            beta={false}
            modifiers={[PlatformCardModifier.OLD, PlatformCardModifier.OVERVIEW]}
          />
       </Tooltip>
        );
      },
      header: t('Coin', { ns: 'overview' }),
    },
    {
      accessorKey: 'summarized_custom_names',
      cell: (info) => {
        const items: string[] = info.getValue() as string[] | [];
        return (
          <IconList items={items} modifiers={[IconListModifier.TABLE_SPACING]} />
        );
      },
      header: t('Custom names', { ns: 'overview' }),
    },
    {
      accessorKey: 'live_balance_spot',
      cell: (info) => {
        const value : number = info.getValue();
        return (
          <div className="ct_tableBase-text-wrap">
            {getFormattedNumber(
              value,
              userSettings?.decimalCharacter,
              userSettings?.thousandCharacter,
            )}
          </div>
        );
      },
      header: t('Live Balance', { ns: 'overview' }),
    },
    {
      accessorKey: 'live_balance_fiat',
      cell: (info) => {
        const value : number = info.getValue();
        return (
          <div className="ct_tableBase-text-wrap">
            {getFormattedNumber(
              value,
              userSettings?.decimalCharacter,
              userSettings?.thousandCharacter,
              userSettings.currency,
            )}
          </div>
        );
      },
      header: `${t('in', { ns: 'overview' })} ${userSettings.currency}`,
    },
    {
      accessorKey: 'imported_balance_spot',
      cell: (info) => {
        const value : number = info.getValue();
        return (
          <div className="ct_tableBase-text-wrap">
            {getFormattedNumber(
              value,
              userSettings?.decimalCharacter,
              userSettings?.thousandCharacter,
            )}
          </div>
        );
      },
      header: t('Imported Balance', { ns: 'overview' }),
    },
    {
      accessorKey: 'imported_balance_fiat',
      cell: (info) => {
        const value : number = info.getValue();
        return (
          <div className="ct_tableBase-text-wrap">
            {getFormattedNumber(
              value,
              userSettings?.decimalCharacter,
              userSettings?.thousandCharacter,
              userSettings.currency,
            )}
          </div>
        );
      },
      header: `${t('in', { ns: 'overview' })} ${userSettings.currency}`,
    },
    {
      accessorKey: 'difference_spot',
      cell: (info) => {
        const value : number = info.getValue();
        return (
          <div className="ct_tableBase-text-wrap">
            {getFormattedNumber(
              value,
              userSettings?.decimalCharacter,
              userSettings?.thousandCharacter,
            )}
          </div>
        );
      },
      header: t('Difference', { ns: 'overview' }),
    },
    {
      accessorKey: 'difference_fiat',
      cell: (info) => {
        const value : number = info.getValue();
        return (
          <div className="ct_tableBase-text-wrap">
            {getFormattedNumber(
              value,
              userSettings?.decimalCharacter,
              userSettings?.thousandCharacter,
              userSettings.currency,
            )}
          </div>
        );
      },
      header: `${t('in', { ns: 'overview' })} ${userSettings.currency}`,
    },
    {
      accessorKey: 'status',
      enableSorting: false,
      cell: (info) => {
        const value: BalanceStatusEnum | undefined = info.getValue() as BalanceStatusEnum
        | undefined;
        if (!value && value !== 0) return '';
        const status = getImportTableBalanceStatus(value);
        return <BalanceStatusTooltip status={status} isDetail={false} />;
      },
      header: '',
    },
  ], []);

  return (
    <>
      <BalanceTableFilters
        ignoreSmallMismatchesProp={
          (location.state as LocationHistoryMismatch !== undefined)
          && (location.state as LocationHistoryMismatch !== null)
          && ((location.state as LocationHistoryMismatch).data !== undefined)
          && ((location.state as LocationHistoryMismatch).data !== null)
            ? (location.state as LocationHistoryMismatch).data! : true
        }
        ignoreUserCurrencyMismatchesProp={
          (location.state as LocationHistoryMismatch !== undefined)
          && (location.state as LocationHistoryMismatch !== null)
          && ((location.state as LocationHistoryMismatch).dataCurrency !== undefined)
          && ((location.state as LocationHistoryMismatch).dataCurrency !== null)
            ? (location.state as LocationHistoryMismatch).dataCurrency! : true
        }
        onSmallMismatchUpdate={(value: boolean) => {
          if (props.setCurrentMismatch) props.setCurrentMismatch(value);
        }}
        onUserCurrencyMismatchUpdate={(value: boolean) => {
          if (props.setUserCurrencyMismatch) props.setUserCurrencyMismatch(value);
        }}
        onFitlerCB={(value: string) => setFilters(value)}
      />
      <br />
      <LegacyTableBase
        requestUri={requestUri}
        filters={filters}
        requiresFiltersForFirstLoad
        apiKind={TableBaseApiKind.LAMINAS}
        columns={balanceTableColumns}
        columnVisibility={columnVisibility}
        initialSorting={initialSorting}
        modifier={TableModifier.BALANCE_TABLE}
        pageSize={pageSize}
        pageIndex={0}
        onError={(e) => handleError(e)}
      />
    </>
  );
}

BlanaceTable.defaultProps = { ...{ } };

export default BlanaceTable;
